import { Component, AfterViewInit } from '@angular/core';

interface PricingPlan {
  id: string;
  name: string;
  description: string;
  vcpu: number;
  ram: string;
  storage: string;
  hourlyRate: number;
  monthlyRate: number;
  yearlyRate?: number;
  popular?: boolean;
}

interface ServiceCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
  plans: PricingPlan[];
}

interface WindowsEdition {
  id: string;
  name: string;
  description: string;
  plans: PricingPlan[];
}

@Component({
  selector: 'app-interactive-pricing-calculator',
  templateUrl: './interactive-pricing-calculator.component.html',
  styleUrls: ['./interactive-pricing-calculator.component.scss']
})
export class InteractivePricingCalculatorComponent implements AfterViewInit {
  // Selected options
  selectedCategory: string = 'linux';
  selectedEdition: string = 'standard'; // For Windows MSSQL
  selectedPlan: string = 'linux-c3-2';
  billingCycle: 'hourly' | 'monthly' | 'yearly' = 'monthly';

  // Service categories with their plans
  serviceCategories: ServiceCategory[] = [
    {
      id: 'linux',
      name: 'Linux Cloud Instances',
      description: 'High-performance Linux cloud instances optimized for various workloads',
      icon: 'bx-linux',
      plans: [
        {
          id: 'linux-c3-2',
          name: 'C3-2',
          description: 'Basic Compute',
          vcpu: 2,
          ram: '4 GB',
          storage: '40 GB NVMe',
          hourlyRate: 0.90,
          monthlyRate: 660,
          popular: false
        },
        {
          id: 'linux-c3-4',
          name: 'C3-4',
          description: 'Standard Compute',
          vcpu: 4,
          ram: '8 GB',
          storage: '80 GB NVMe',
          hourlyRate: 1.80,
          monthlyRate: 1320,
          popular: false
        },
        {
          id: 'linux-c3-8',
          name: 'C3-8',
          description: 'High Performance',
          vcpu: 8,
          ram: '16 GB',
          storage: '160 GB NVMe',
          hourlyRate: 3.60,
          monthlyRate: 2640,
          popular: true
        },
        {
          id: 'linux-c3-16',
          name: 'C3-16',
          description: 'Enterprise Compute',
          vcpu: 16,
          ram: '32 GB',
          storage: '320 GB NVMe',
          hourlyRate: 7.20,
          monthlyRate: 5280,
          popular: false
        }
      ]
    },
    {
      id: 'windows',
      name: 'Windows Cloud Instances',
      description: 'Windows Server instances with full compatibility and optimized performance',
      icon: 'bx-windows',
      plans: [
        {
          id: 'windows-c3-2',
          name: 'C3-2',
          description: 'Basic Compute',
          vcpu: 2,
          ram: '4 GB',
          storage: '40 GB NVMe',
          hourlyRate: 1.20,
          monthlyRate: 880,
          popular: false
        },
        {
          id: 'windows-c3-4',
          name: 'C3-4',
          description: 'Standard Compute',
          vcpu: 4,
          ram: '8 GB',
          storage: '80 GB NVMe',
          hourlyRate: 2.40,
          monthlyRate: 1760,
          popular: false
        },
        {
          id: 'windows-c3-8',
          name: 'C3-8',
          description: 'High Performance',
          vcpu: 8,
          ram: '16 GB',
          storage: '160 GB NVMe',
          hourlyRate: 4.80,
          monthlyRate: 3520,
          popular: true
        },
        {
          id: 'windows-c3-16',
          name: 'C3-16',
          description: 'Enterprise Compute',
          vcpu: 16,
          ram: '32 GB',
          storage: '320 GB NVMe',
          hourlyRate: 9.60,
          monthlyRate: 7040,
          popular: false
        }
      ]
    }
  ];

  // Windows with MSSQL editions
  windowsMssqlEditions: WindowsEdition[] = [
    {
      id: 'standard',
      name: 'Standard Edition',
      description: 'Windows with SQL Server Standard Edition - Ideal for small to medium businesses',
      plans: [
        {
          id: 'mssql-standard-2',
          name: 'C3-2',
          description: 'Basic Compute',
          vcpu: 2,
          ram: '4 GB',
          storage: '40 GB NVMe + 50 GB SSD',
          hourlyRate: 2.50,
          monthlyRate: 1830,
          popular: false
        },
        {
          id: 'mssql-standard-4',
          name: 'C3-4',
          description: 'Standard Compute',
          vcpu: 4,
          ram: '8 GB',
          storage: '80 GB NVMe + 100 GB SSD',
          hourlyRate: 5.00,
          monthlyRate: 3660,
          popular: false
        },
        {
          id: 'mssql-standard-8',
          name: 'C3-8',
          description: 'High Performance',
          vcpu: 8,
          ram: '16 GB',
          storage: '160 GB NVMe + 200 GB SSD',
          hourlyRate: 10.00,
          monthlyRate: 7320,
          popular: true
        },
        {
          id: 'mssql-standard-16',
          name: 'C3-16',
          description: 'Enterprise Compute',
          vcpu: 16,
          ram: '32 GB',
          storage: '320 GB NVMe + 400 GB SSD',
          hourlyRate: 20.00,
          monthlyRate: 14640,
          popular: false
        }
      ]
    },
    {
      id: 'web',
      name: 'Web Edition',
      description: 'Windows with SQL Server Web Edition - Cost-effective solution for web applications',
      plans: [
        {
          id: 'mssql-web-2',
          name: 'C3-2',
          description: 'Basic Compute',
          vcpu: 2,
          ram: '4 GB',
          storage: '40 GB NVMe + 50 GB SSD',
          hourlyRate: 1.80,
          monthlyRate: 1320,
          popular: false
        },
        {
          id: 'mssql-web-4',
          name: 'C3-4',
          description: 'Standard Compute',
          vcpu: 4,
          ram: '8 GB',
          storage: '80 GB NVMe + 100 GB SSD',
          hourlyRate: 3.60,
          monthlyRate: 2640,
          popular: false
        },
        {
          id: 'mssql-web-8',
          name: 'C3-8',
          description: 'High Performance',
          vcpu: 8,
          ram: '16 GB',
          storage: '160 GB NVMe + 200 GB SSD',
          hourlyRate: 7.20,
          monthlyRate: 5280,
          popular: true
        },
        {
          id: 'mssql-web-16',
          name: 'C3-16',
          description: 'Enterprise Compute',
          vcpu: 16,
          ram: '32 GB',
          storage: '320 GB NVMe + 400 GB SSD',
          hourlyRate: 14.40,
          monthlyRate: 10560,
          popular: false
        }
      ]
    }
  ];

  // Get selected category
  get selectedCategoryData(): ServiceCategory | undefined {
    if (this.selectedCategory === 'windows-mssql') {
      return undefined;
    }
    return this.serviceCategories.find(category => category.id === this.selectedCategory);
  }

  // Get selected edition for Windows MSSQL
  get selectedEditionData(): WindowsEdition | undefined {
    if (this.selectedCategory !== 'windows-mssql') {
      return undefined;
    }
    return this.windowsMssqlEditions.find(edition => edition.id === this.selectedEdition);
  }

  // Get selected plan
  get selectedPlanData(): PricingPlan | undefined {
    if (this.selectedCategory === 'windows-mssql') {
      const edition = this.selectedEditionData;
      return edition?.plans.find(plan => plan.id === this.selectedPlan);
    } else {
      const category = this.selectedCategoryData;
      return category?.plans.find(plan => plan.id === this.selectedPlan);
    }
  }

  // Get plans based on selection
  get selectedPlans(): PricingPlan[] {
    if (this.selectedCategory === 'windows-mssql') {
      const edition = this.selectedEditionData;
      return edition ? edition.plans : [];
    } else {
      const category = this.selectedCategoryData;
      return category ? category.plans : [];
    }
  }

  // Get formatted price based on billing cycle
  getFormattedPrice(): string {
    const plan = this.selectedPlanData;
    if (!plan) return '₹0';

    switch (this.billingCycle) {
      case 'hourly':
        return `₹${plan.hourlyRate.toFixed(2)}`;
      case 'monthly':
        return `₹${plan.monthlyRate.toLocaleString()}`;
      case 'yearly':
        return plan.yearlyRate ? `₹${plan.yearlyRate.toLocaleString()}` : `₹${(plan.monthlyRate * 12).toLocaleString()}`;
      default:
        return `₹${plan.monthlyRate.toLocaleString()}`;
    }
  }

  // Handle category change
  onCategoryChange(categoryId: string): void {
    this.selectedCategory = categoryId;
    // Reset to first plan in the category/edition
    if (categoryId === 'windows-mssql') {
      // Reset to first edition and first plan in that edition
      this.selectedEdition = this.windowsMssqlEditions[0].id;
      this.selectedPlan = this.windowsMssqlEditions[0].plans[0].id;
    } else {
      const category = this.serviceCategories.find(cat => cat.id === categoryId);
      if (category && category.plans.length > 0) {
        this.selectedPlan = category.plans[0].id;
      }
    }
  }

  // Handle edition change (for Windows MSSQL)
  onEditionChange(editionId: string): void {
    this.selectedEdition = editionId;
    // Reset to first plan in the edition
    const edition = this.windowsMssqlEditions.find(ed => ed.id === editionId);
    if (edition && edition.plans.length > 0) {
      this.selectedPlan = edition.plans[0].id;
    }
  }

  // Handle plan change
  onPlanChange(planId: string): void {
    this.selectedPlan = planId;
  }

  // Handle billing cycle change
  onBillingCycleChange(cycle: 'hourly' | 'monthly' | 'yearly'): void {
    this.billingCycle = cycle;
  }

  // After view init, ensure icons are visible
  ngAfterViewInit(): void {
    // Small delay to ensure DOM is fully rendered
    setTimeout(() => {
      this.ensureIconsVisible();
      this.forceReflow();
    }, 300);
  }

  // Ensure icons are visible
  private ensureIconsVisible(): void {
    try {
      // Debug logging
      console.log('Ensuring icons visibility...');
      
      // Ensure Boxicons visibility
      const boxIcons = document.querySelectorAll('.interactive-pricing-section .bx');
      console.log('Found', boxIcons.length, 'Boxicons');
      boxIcons.forEach(icon => {
        // Ensure visibility
        const htmlElement = icon as HTMLElement;
        htmlElement.style.visibility = 'visible';
        htmlElement.style.display = 'inline-block';
        
        // Force font family
        htmlElement.style.fontFamily = 'boxicons';
      });

      // Ensure Font Awesome icons visibility
      const faSolidIcons = document.querySelectorAll('.interactive-pricing-section .fas');
      console.log('Found', faSolidIcons.length, 'Font Awesome Solid icons');
      faSolidIcons.forEach(icon => {
        const htmlElement = icon as HTMLElement;
        htmlElement.style.visibility = 'visible';
        htmlElement.style.display = 'inline-block';
        htmlElement.style.fontFamily = '"Font Awesome 5 Free"';
        htmlElement.style.fontWeight = '900';
      });

      const faBrandIcons = document.querySelectorAll('.interactive-pricing-section .fab');
      console.log('Found', faBrandIcons.length, 'Font Awesome Brand icons');
      faBrandIcons.forEach(icon => {
        const htmlElement = icon as HTMLElement;
        htmlElement.style.visibility = 'visible';
        htmlElement.style.display = 'inline-block';
        htmlElement.style.fontFamily = '"Font Awesome 5 Brands"';
        htmlElement.style.fontWeight = '400';
      });
      
      // Ensure combo icons visibility
      const comboIcons = document.querySelectorAll('.interactive-pricing-section .icon-combo .combo-icon');
      console.log('Found', comboIcons.length, 'combo icons');
      comboIcons.forEach(icon => {
        const htmlElement = icon as HTMLElement;
        htmlElement.style.visibility = 'visible';
        htmlElement.style.display = 'block';
        htmlElement.style.position = 'absolute';
        htmlElement.style.zIndex = '2';
        htmlElement.style.color = 'inherit'; // Ensure icons inherit color
        
        // Ensure Font Awesome styling
        if (htmlElement.classList.contains('fab')) {
          htmlElement.style.fontFamily = '"Font Awesome 5 Brands"';
          htmlElement.style.fontWeight = '400';
        } else if (htmlElement.classList.contains('fas')) {
          htmlElement.style.fontFamily = '"Font Awesome 5 Free"';
          htmlElement.style.fontWeight = '900';
        }
      });
      
      // Ensure icon combo containers are visible
      const iconCombos = document.querySelectorAll('.interactive-pricing-section .icon-combo');
      console.log('Found', iconCombos.length, 'icon combos');
      iconCombos.forEach(combo => {
        const htmlElement = combo as HTMLElement;
        htmlElement.style.display = 'flex';
        htmlElement.style.alignItems = 'center';
        htmlElement.style.justifyContent = 'center';
        htmlElement.style.width = '100%';
        htmlElement.style.height = '100%';
        htmlElement.style.position = 'relative';
        htmlElement.style.border = '1px solid red'; // Debug border
      });
      
      // Specific handling for MSSQL icon combo
      const mssqlIconCombos = document.querySelectorAll('.interactive-pricing-section .mssql-icon .icon-combo');
      console.log('Found', mssqlIconCombos.length, 'MSSQL icon combos');
      mssqlIconCombos.forEach(combo => {
        const htmlElement = combo as HTMLElement;
        htmlElement.style.position = 'relative';
        htmlElement.style.width = '100%';
        htmlElement.style.height = '100%';
      });
      
      // Debug: Check if Windows and Database icons are present
      const windowsIcons = document.querySelectorAll('.interactive-pricing-section .fab.fa-windows');
      const databaseIcons = document.querySelectorAll('.interactive-pricing-section .fas.fa-database');
      console.log('Found', windowsIcons.length, 'Windows icons');
      console.log('Found', databaseIcons.length, 'Database icons');
      
      // Debug: Check specifically for combo icons
      const windowsComboIcons = document.querySelectorAll('.interactive-pricing-section .icon-combo .windows-combo');
      const databaseComboIcons = document.querySelectorAll('.interactive-pricing-section .icon-combo .database-combo');
      console.log('Found', windowsComboIcons.length, 'Windows combo icons');
      console.log('Found', databaseComboIcons.length, 'Database combo icons');
      
      // Additional debugging for combo icons
      windowsComboIcons.forEach(icon => {
        const htmlElement = icon as HTMLElement;
        console.log('Windows combo icon style:', htmlElement.style);
        htmlElement.style.color = 'white'; // Explicitly set color
      });
      
      databaseComboIcons.forEach(icon => {
        const htmlElement = icon as HTMLElement;
        console.log('Database combo icon style:', htmlElement.style);
        htmlElement.style.color = 'white'; // Explicitly set color
      });
    } catch (error) {
      console.log('Error ensuring icons visibility:', error);
    }
  }

  // Force reflow to ensure icons render
  private forceReflow(): void {
    try {
      // Force a reflow by reading and writing to the DOM
      const section = document.querySelector('.interactive-pricing-section');
      if (section) {
        const htmlElement = section as HTMLElement;
        // Trigger reflow
        const originalDisplay = htmlElement.style.display;
        htmlElement.style.display = 'none';
        // Force reflow
        section.clientHeight; // This forces a reflow
        // Restore display
        htmlElement.style.display = originalDisplay;
      }
    } catch (error) {
      console.log('Error forcing reflow:', error);
    }
  }
}